import React, { Fragment, createContext, useContext } from 'react';
import { Dialog as HeadlessDialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

// IDI-87: DialogHeader used to render its close button only when the caller
// explicitly passed onClose. Two of thirty-two call sites forgot the prop
// (QuoteDetail's version-viewer modal and WorkOrderDetail's crew modal), so
// both modals shipped without a visible close control while remaining
// Escape- and backdrop-dismissable. 2 of 32 means the thirty-third is a
// coin flip — so the class is closed here by making DialogHeader inherit
// the dismiss handler from Dialog through context. Callers that WANT to
// suppress the button (a modal that must not be dismissable at all) don't
// pass onClose to Dialog either — no Escape, no backdrop, no X. If they
// want Escape/backdrop but no X, they can pass onClose={undefined} on the
// DialogHeader explicitly, which is a loud opt-out and readable in review.
const DialogCloseContext = createContext(null);

export function Dialog({ open, onClose, children, size = 'md' }) {
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    // Iter18 R2 §2.2: `wide` = 38rem, sized to comfortably host the Add User form
    // (name + email + seat radios + role radios + permission toggles) without the
    // inline permission checklist wrapping awkwardly.
    wide: 'max-w-[38rem]',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    report: 'max-w-5xl',
    full: 'max-w-full mx-4',
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <HeadlessDialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <HeadlessDialog.Panel
                className={clsx('w-full transform overflow-hidden bg-white shadow-xl transition-all', sizeClasses[size])}
                style={{ borderRadius: 'var(--radius-md)' }}
              >
                <DialogCloseContext.Provider value={onClose}>
                  {children}
                </DialogCloseContext.Provider>
              </HeadlessDialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </HeadlessDialog>
    </Transition>
  );
}

// IDI-87: DialogHeader inherits its close handler from Dialog through
// context by default. `onClose` prop is still accepted as an override:
//   - undefined (default) -> use whatever Dialog was wired with
//   - a function          -> use this specific handler (e.g. confirm-first)
//   - explicit null       -> suppress the X even when Dialog has onClose
// The third case is deliberate opt-out for modals that need Escape/backdrop
// dismissal but no header X (rare; loud in review; documented here).
export function DialogHeader({ children, onClose }) {
  const contextOnClose = useContext(DialogCloseContext);
  // onClose === null means "explicitly suppress" — don't fall back.
  // onClose === undefined means "not overridden" — fall back to context.
  const effectiveOnClose = onClose === null ? null : (onClose ?? contextOnClose);
  return (
    <div className="flex items-center justify-between border-b border-[color:var(--color-border)] px-6 py-4">
      <HeadlessDialog.Title className="text-lg font-semibold text-gray-900">
        {children}
      </HeadlessDialog.Title>
      {effectiveOnClose && (
        <button
          onClick={effectiveOnClose}
          aria-label="Close dialog"
          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          style={{ borderRadius: 'var(--radius-md)' }}
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

export function DialogBody({ children, className }) {
  return (
    <div className={clsx('px-6 py-4', className)}>
      {children}
    </div>
  );
}

export function DialogFooter({ children, className }) {
  return (
    <div className={clsx('flex items-center justify-end gap-3 border-t border-[color:var(--color-border)] px-6 py-4', className)}>
      {children}
    </div>
  );
}
